<script lang="ts">
	import {
		DataHandlerDeviceAdmin,
		DataHandlerUserAdmin,
		Device,
		DeviceRPi,
		User,
	} from "luxedo-data"
	import { LuxedoRPC } from "luxedo-rpc"
	import { onMount } from "svelte"
	import { openOverlay, openPopupOverlay } from "overlay"
	import { Toast } from "toaster"
	import { CopyButton } from "misc-ui"
	import DeviceOwners from "./DeviceOwners.svelte"
	import { openDeviceImageCaptureOverlay } from "../../../reusable/overlays/device-image-capture"
	import HdmiBoostInput from "./actions/HDMIBoostInput.svelte"
	import RemoteApp from "../remote/RemoteApp.svelte"

	const SSH_PORT = 31001

	interface Props {
		device: Device
	}

	let { device }: Props = $props()

	let adminInfo: { [index: string]: any } = $state({})
	let owners: Array<User> = $state([])

	async function updateOwners() {
		const ownerIDs = await LuxedoRPC.api.admin.admin_get_device_owners(
			device.id
		)
		await DataHandlerUserAdmin.pull(ownerIDs)
		owners = DataHandlerUserAdmin.getMany(ownerIDs)
	}

	function showSSHInstructions() {
		openPopupOverlay(
			{
				prompt: `Open the tunnel, then load up your Ubuntu-for-windows and check it's ssh config at ~/.ssh/config. Ensure that the jumplink uses port 31001 and points to PROD.`,
			},
			{
				heading: "SSH Instructions",
			}
		)
	}

	async function openSSH() {
		try {
			await LuxedoRPC.api.admin.device_remote_tunnel_open(device.id, SSH_PORT)
			Toast.success("SSH tunnel is now open.")
		} catch (e) {
			Toast.error("Unable to open SSH tunnel. ")
			console.error("Unable to open SSH tunnel.", e)
		}
	}

	async function closeSSH() {
		try {
			await LuxedoRPC.api.admin.device_remote_tunnel_close(device.id)
			Toast.success("SSH tunnel is now closed.")
		} catch (e) {
			Toast.error("Unable to close SSH tunnel. ")
			console.error("Unable to close SSH tunnel.", e)
		}
	}

	function captureImages() {
		openDeviceImageCaptureOverlay(device)
	}

	async function fixStuckUpdate() {
		try {
			const data = await LuxedoRPC.api.admin.admin_fix_stuck_update(device.id)
			console.log("Device update fix result", data)
			if (data.fixed) Toast.success("Device should now be updating...")
			else Toast.error(`Unable to fix stuck update. Info: ${data.info}`)
		} catch (e) {
			console.error("ERROR while fixing update", e)
			Toast.error("An error occurred while trying to fix stuck update...")
		}
	}

	function openProjectorRemote() {
		const url = `/admin/device/${device.id}/remote`
		const width = 400
		const height = 500

		// Calculate the position to center the popup on the screen
		const left = window.screenX + (window.outerWidth - width) / 2
		const top = window.screenY + (window.outerHeight - height) / 2

		// Specify window features: width, height, left, top, and additional options
		const specs = `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`

		// Open the popup window
		window.open(url, "popupWindow", specs)
	}

	onMount(async () => {
		await updateOwners()

		// get device admin info
		await DataHandlerDeviceAdmin.pull([device.id])
		adminInfo = await DataHandlerDeviceAdmin.get(device.id)
	})
</script>

<div class="info">
	<h2>Device Info</h2>
	<div class="info-wrapper">
		<span class="info-title">ID: </span>
		<span>{device.id}</span>
	</div>
	<div class="info-wrapper">
		<span class="info-title">Version: </span>
		<span>{device.firmwareVersion}</span>
	</div>
	<div class="info-wrapper">
		<span class="info-title">Name: </span>
		<span>{`${device.name}`}</span>
	</div>
	<div class="info-wrapper">
		<span class="info-title">Type: </span>
		<span>{Device.getDeviceTypeName(device.typeId)}</span>
	</div>
	{#if device.supportsPassword}
		<div class="info-wrapper">
			<span class="info-title">Password: </span>
			<span class="inline-button-container"
				>{adminInfo.password}
				<CopyButton textContent={adminInfo.password} />
			</span>
		</div>
		<div class="info-wrapper">
			<span class="info-title">SSH Tunnel: </span>
			<span class="flex-row">
				<span class="button-row">
					<button class="small" title="Open Tunnel" onclick={openSSH}
						>Open</button
					>
					<button class="small" title="Close Tunnel" onclick={closeSSH}
						>Close</button
					>
				</span>
				<button
					class="small"
					onclick={showSSHInstructions}
					title="SSH Instructions">?</button
				>
			</span>
		</div>
	{/if}
	<div class="info-wrapper">
		<span class="info-title">Active Owners: </span>
		<DeviceOwners {owners} {device} {updateOwners} />
	</div>
	{#if device instanceof DeviceRPi}
		<div class="info-wrapper">
			<span class="info-title">Actions:</span>
			<div class="flex-column">
				<button
					class="small"
					title="Capture Test Images"
					onclick={captureImages}>Capture Images</button
				>

				<button
					class="small"
					title="Open RS232 Remote"
					onclick={openProjectorRemote}>Remote Control</button
				>

				{#if device.compareVersion("2.12.3") < 1}
					<button
						class="small"
						title="Fix device stuck updating from v2.12.3"
						onclick={fixStuckUpdate}>Fix Stuck Update</button
					>
				{/if}

				{#if device.compareVersion("3.4.0") >= 0}
					<HdmiBoostInput {device} />
				{/if}
			</div>
		</div>
	{/if}
</div>

<style>
	.button-row {
		margin-right: 1rem;
	}

	.flex-column {
		gap: 0.1rem;
		align-items: flex-end;
	}
</style>
